import { apiGet, apiPatch, apiPost } from "./client";
import {
  createParty,
  deactivateParty,
  listParties,
  patchParty,
  reactivateParty,
} from "./parties";

export type CustomerRow = {
  id: string;
  code: string | null;
  name: string;
  priceListId: string | null;
  deactivatedAt: string | null;
  updatedAt: string;
  /** Open SO AR (total − paid); "0.00" when clear */
  outstanding: string;
};

export type CustomerListPage = {
  customers: CustomerRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type CustomerListSort = "name" | "code";

/** Customer-role parties — plan/37 (`/parties?role=customer`). */
export async function listCustomers(
  companyRef: string,
  opts?: {
    includeDeactivated?: boolean;
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: CustomerListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
): Promise<CustomerListPage> {
  const page = await listParties(companyRef, {
    role: "customer",
    includeDeactivated: opts?.includeDeactivated,
    q: opts?.q,
    limit: opts?.limit,
    cursor: opts?.cursor,
    sort: opts?.sort,
    sortDir: opts?.sortDir,
    signal: opts?.signal,
  });
  return {
    customers: page.parties.map((p) => ({
      id: p.id,
      code: p.code,
      name: p.name,
      priceListId: p.priceListId,
      deactivatedAt: p.deactivatedAt,
      updatedAt: p.updatedAt,
      outstanding: p.outstandingAr ?? p.outstanding,
    })),
    hasMore: page.hasMore,
    nextCursor: page.nextCursor,
    total: page.total,
  };
}

export async function deactivateCustomer(
  companyRef: string,
  customerId: string,
) {
  const res = await deactivateParty(companyRef, customerId);
  return { customer: res.party };
}

export async function reactivateCustomer(
  companyRef: string,
  customerId: string,
) {
  const res = await reactivateParty(companyRef, customerId);
  return { customer: res.party };
}

export async function createCustomer(
  companyRef: string,
  body: { name: string; code?: string; priceListId?: string },
) {
  const res = await createParty(companyRef, {
    name: body.name,
    code: body.code,
    isCustomer: true,
    isSupplier: false,
    priceListId: body.priceListId,
  });
  return {
    customer: {
      id: res.party.id,
      name: res.party.name,
      priceListId: res.party.priceListId,
    },
  };
}

export async function setCustomerPriceList(
  companyRef: string,
  customerId: string,
  priceListId: string | null,
) {
  const res = await patchParty(companyRef, customerId, { priceListId });
  return { customer: { id: res.party.id, priceListId: res.party.priceListId } };
}

/** Identity rename — may apply or queue a change request (plan/16). */
export function renameCustomer(
  companyRef: string,
  customerId: string,
  name: string,
) {
  return apiPatch<
    | {
        applied: true;
        customer: { id: string; name: string };
      }
    | {
        applied: false;
        changeRequest: {
          id: string;
          status: string;
          permissionKey: string;
          category: string;
        };
      }
  >(`/c/${companyRef}/customers/${customerId}`, { name });
}

export type SoListRow = {
  id: string;
  number: string;
  lifecycle: string;
  customerName: string;
  customerId: string;
  customerRef?: string | null;
  grossTotal: string;
  deliver: string;
  pay: string;
  amountPaid: string;
  qtyDelivered: string;
  qtyOrdered: string;
  total: string;
  paymentDueOn?: string | null;
  deliverBy?: string | null;
  /** Business / transaction date YYYY-MM-DD — plan/35 */
  docDate?: string | null;
  updatedAt?: string | null;
};

export type SalesOrderListPage = {
  orders: SoListRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type SoListSort =
  | "number"
  | "customerName"
  | "grossTotal"
  | "lifecycle"
  | "dueAt"
  | "docDate";

export function listSalesOrders(
  companyRef: string,
  opts?: {
    q?: string;
    limit?: number;
    cursor?: string | null;
    lifecycle?: string;
    tagId?: string;
    assignedToMe?: boolean;
    unassigned?: boolean;
    sort?: SoListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (
    opts?.lifecycle === "draft" ||
    opts?.lifecycle === "open" ||
    opts?.lifecycle === "void"
  ) {
    params.set("lifecycle", opts.lifecycle);
  }
  if (opts?.tagId) params.set("tagId", opts.tagId);
  if (opts?.assignedToMe) params.set("assignedToMe", "1");
  if (opts?.unassigned) params.set("unassigned", "1");
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<SalesOrderListPage>(
    `/c/${companyRef}/sales/orders${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function createSalesOrder(
  companyRef: string,
  body: {
    customerId: string;
    lines: Array<{
      productId: string;
      qty: string;
      unitPrice?: string;
      discountPct?: string;
    }>;
    discountPct?: string;
    taxRatePct?: string;
    taxPriceMode?: "inclusive" | "exclusive";
    number?: string;
    submit?: boolean;
    customerRef?: string;
    paymentDueOn?: string;
    deliverBy?: string;
    docDate?: string;
    confirmOverBackdate?: boolean;
    priceListId?: string;
    confirmDiscountOverCap?: boolean;
    overrideRequestId?: string;
  },
) {
  return apiPost<{
    order: { id: string; number: string; grossTotal: string };
    discountOverCap?: boolean;
  }>(`/c/${companyRef}/sales/orders`, body);
}

export function patchSalesOrderDraft(
  companyRef: string,
  orderId: string,
  body: {
    customerRef?: string | null;
    notes?: string | null;
    paymentDueOn?: string | null;
    deliverBy?: string | null;
    docDate?: string;
    confirmOverBackdate?: boolean;
  },
) {
  return apiPatch<{ order: { id: string; number: string } }>(
    `/c/${companyRef}/sales/orders/${orderId}`,
    body,
  );
}

export function getSalesOrder(companyRef: string, orderId: string) {
  return apiGet<{
    order: SoListRow & {
      warehouseId: string | null;
      netTotal: string;
      taxTotal: string;
      discountPct: string;
      taxPriceMode: string;
      taxRatePct: string;
      paymentDueOn: string | null;
      deliverBy: string | null;
      docDate: string | null;
      customerRef: string | null;
      notes: string | null;
    };
    lines: Array<{
      id: string;
      productId: string;
      productName: string;
      description: string | null;
      qtyOrdered: string;
      unitPrice: string;
      discountPct: string;
      lineGross: string;
      qtyDelivered: string;
      qtyOpen: string;
      trackLot?: boolean;
      trackSerial?: boolean;
    }>;
    deliveries: Array<{
      id: string;
      number: string;
      lifecycle: string;
      postedAt: string | null;
    }>;
    payments: Array<{
      id: string;
      number: string;
      direction: string;
      lifecycle: string;
      amount: string;
      postedAt: string | null;
    }>;
  }>(`/c/${companyRef}/sales/orders/${orderId}`);
}

export function getSalesDelivery(companyRef: string, deliveryId: string) {
  return apiGet<{
    delivery: {
      id: string;
      number: string;
      lifecycle: string;
      salesOrderId: string;
      soNumber: string;
      warehouseId: string;
      warehouseName: string;
      warehouseCode: string;
      notes: string | null;
      postedAt: string | null;
    };
    lines: Array<{
      id: string;
      productId: string;
      productName: string;
      salesOrderLineId: string;
      qty: string;
      unitCost: string;
      lotCode: string | null;
      serialCode: string | null;
    }>;
  }>(`/c/${companyRef}/sales/deliveries/${deliveryId}`);
}

export function postSalesDelivery(
  companyRef: string,
  orderId: string,
  body: {
    warehouseId?: string;
    confirmOverDeliver?: boolean;
    confirmOversell?: boolean;
    overrideRequestId?: string;
    idempotencyKey?: string;
    lines: Array<{
      salesOrderLineId: string;
      qty: string;
      lotCode?: string;
      serialCode?: string;
    }>;
  },
) {
  return apiPost<{
    delivery: { id: string; number: string };
    so: { deliver: string; pay: string };
    overDeliver?: boolean;
    oversell?: boolean;
    replayed?: boolean;
  }>(`/c/${companyRef}/sales/orders/${orderId}/deliveries`, body);
}

export function submitSalesOrder(companyRef: string, orderId: string) {
  return apiPost<{ order: { id: string; number: string; lifecycle: string } }>(
    `/c/${companyRef}/sales/orders/${orderId}/submit`,
  );
}

export function cancelSalesOrder(companyRef: string, orderId: string) {
  return apiPost<{ order: { id: string; number: string; lifecycle: string } }>(
    `/c/${companyRef}/sales/orders/${orderId}/cancel`,
  );
}

export function voidSalesOrder(companyRef: string, orderId: string) {
  return apiPost<{ order: { id: string; number: string; lifecycle: string } }>(
    `/c/${companyRef}/sales/orders/${orderId}/void`,
  );
}

export type CustomerPaymentListRow = {
  id: string;
  number: string;
  direction: string;
  lifecycle: string;
  amount: string;
  bankRef: string | null;
  postedAt: string | null;
  updatedAt: string;
  customerId: string;
  customerName: string;
  payMethodName: string | null;
};

export type CustomerPaymentListPage = {
  payments: CustomerPaymentListRow[];
  hasMore?: boolean;
  nextCursor?: string | null;
  total?: number;
};

export type CustomerPaymentListSort =
  | "number"
  | "customerName"
  | "amount"
  | "lifecycle";

export function listCustomerPayments(
  companyRef: string,
  opts?: {
    q?: string;
    lifecycle?: "draft" | "posted" | "void";
    limit?: number;
    cursor?: string | null;
    sort?: CustomerPaymentListSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.lifecycle) params.set("lifecycle", opts.lifecycle);
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<CustomerPaymentListPage>(
    `/c/${companyRef}/sales/payments${qs ? `?${qs}` : ""}`,
    { signal: opts?.signal },
  );
}

export function voidCustomerPayment(
  companyRef: string,
  paymentId: string,
  body?: { idempotencyKey?: string },
) {
  return apiPost<{
    payment: { id: string; number: string; lifecycle: string };
    replayed?: boolean;
  }>(`/c/${companyRef}/sales/payments/${paymentId}/void`, body ?? {});
}

export function getCustomerPayment(companyRef: string, paymentId: string) {
  return apiGet<{
    payment: {
      id: string;
      number: string;
      direction: string;
      lifecycle: string;
      amount: string;
      bankRef: string | null;
      notes: string | null;
      postedAt: string | null;
      customerId: string;
      customerName: string;
      payMethodId: string | null;
      payMethodName: string | null;
      payMethodCode: string | null;
    };
    allocations: Array<{
      id: string;
      salesOrderId: string;
      amount: string;
      soNumber: string;
    }>;
  }>(`/c/${companyRef}/sales/payments/${paymentId}`);
}

export function postCustomerPayment(
  companyRef: string,
  body: {
    customerId: string;
    salesOrderId: string;
    amount: string;
    direction?: "from_customer" | "to_customer";
    payMethodId?: string;
    bankRef?: string;
    idempotencyKey?: string;
    confirmOverpay?: boolean;
  },
) {
  return apiPost<{
    payment: { id: string; number: string };
    so: { deliver: string; pay: string; amountPaid: string };
    overpay?: boolean;
    replayed?: boolean;
  }>(`/c/${companyRef}/sales/payments`, body);
}

export type ToCollectSort =
  | "number"
  | "customerName"
  | "outstanding"
  | "paymentDueOn";

export type ToDeliverSort =
  | "number"
  | "customerName"
  | "deliver"
  | "qtyDelivered";

export type ToCollectRow = SoListRow & {
  outstanding: string;
  paymentDueOn: string | null;
};

export function listToCollect(
  companyRef: string,
  opts?: {
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: ToCollectSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<{
    orders: ToCollectRow[];
    hasMore?: boolean;
    nextCursor?: string | null;
    total?: number;
  }>(`/c/${companyRef}/sales/to-collect${qs ? `?${qs}` : ""}`, { signal: opts?.signal });
}

export function listToDeliver(
  companyRef: string,
  opts?: {
    q?: string;
    limit?: number;
    cursor?: string | null;
    sort?: ToDeliverSort;
    sortDir?: "asc" | "desc";
    signal?: AbortSignal;
  },
) {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set("q", opts.q.trim());
  if (opts?.limit != null) params.set("limit", String(opts.limit));
  if (opts?.cursor) params.set("cursor", opts.cursor);
  if (opts?.sort && opts.sort !== "number") params.set("sort", opts.sort);
  if (opts?.sortDir === "desc") params.set("sortDir", "desc");
  const qs = params.toString();
  return apiGet<{
    orders: SoListRow[];
    hasMore?: boolean;
    nextCursor?: string | null;
    total?: number;
  }>(`/c/${companyRef}/sales/to-deliver${qs ? `?${qs}` : ""}`, { signal: opts?.signal });
}
